/**
 * Buying an engine inside the phone app — Apple's App Store on iOS, Google Play on Android.
 *
 * Both shops require that digital content bought inside an app is bought through them, so in the app the
 * price is a store sheet: one tap and Face ID (or Play's fingerprint), no card, no leaving the screen.
 * Everywhere else — the Tesla's browser, a laptop, the phone's browser — nothing changes and Stripe keeps
 * the sale.
 *
 * The web page never decides who owns what: it hands the store's proof to our server, which checks it
 * (Apple's signature against Apple's root certificate; Google's token against the Play Developer API) and
 * writes the purchase down. Only then does the app tell the store the transaction is finished.
 */
import { getDeviceId, addPurchase, getAccountEmail, getPurchases, syncEntitlements } from './access';

type IapEvent = {
  type: 'purchased' | 'cancelled' | 'error' | 'pending' | 'price';
  product?: string;
  restored?: boolean;
  message?: string;
  /** which till this came from */
  store?: 'apple' | 'google';
  /** Apple: the signed transaction */
  jws?: string;
  /** Google: the opaque purchase token, plus what the server needs to pin it to our app */
  purchaseToken?: string;
  packageName?: string;
  orderId?: string;
};

declare global {
  interface Window {
    __roarIapReady?: boolean;
    __roarIapStore?: 'apple' | 'google';
    RoarBridge?: { postMessage: (m: string) => void };
  }
}

const inApp = () => typeof navigator !== 'undefined' && /ROARDRIVE-App/.test(navigator.userAgent);

/** true when this screen can put an engine through the phone's till. Google's only once our server has
 *  said it can verify a Play purchase (it cannot without a service account, and the Android app ships as
 *  an APK, not through Play): until then that phone buys through the web's Stripe checkout, like the
 *  site. Apple's is trusted unless the server says otherwise. */
export function iapAvailable(): boolean {
  if (!inApp() || !window.__roarIapReady) return false;
  return storeOf() === 'google' ? storeOn === true : storeOn !== false;
}

/** 'apple' | 'google'. Before the till announces itself the app's own user agent says which phone this
 *  is (main.dart sends 'ROARDRIVE-App/1.0 (android)'), so an Android phone never reads "App Store". */
function storeOf(e?: IapEvent): 'apple' | 'google' {
  return e?.store || window.__roarIapStore || (/ROARDRIVE-App\/[^ ]* \(android\)/.test(navigator.userAgent) ? 'google' : 'apple');
}
export function iapStore(): 'apple' | 'google' { return storeOf(); }

/** what buyEngine answers for an engine that has no product in this store yet */
export const NOT_ON_SALE = 'this engine is not on sale in the app yet';

/** the proof, and where it has to be checked: the two shops do not hand over the same thing */
function proofFor(e: IapEvent, carId?: string): { url: string; body: Record<string, string> } | null {
  const base: Record<string, string> = { deviceId: getDeviceId(), email: getAccountEmail() || '' };
  if (carId) base.carId = carId;
  if (storeOf(e) === 'google') {
    if (!e.purchaseToken || !e.product) return null;
    return {
      url: '/api/iap/google',
      body: { ...base, purchaseToken: e.purchaseToken, productId: e.product, packageName: e.packageName || '', orderId: e.orderId || '' },
    };
  }
  if (!e.jws) return null;
  return { url: '/api/iap/apple', body: { ...base, jws: e.jws } };
}

/** waits for the app to say the till is there (it announces itself on every page load) AND for our
 *  server to say this phone's till is one it can verify */
export function onIapReady(cb: () => void): () => void {
  let live = true;
  const go = () => { void loadProducts().then(() => { if (live && iapAvailable()) cb(); }); };
  if (inApp() && window.__roarIapReady) go();
  else window.addEventListener('roariapready', go);
  return () => { live = false; window.removeEventListener('roariapready', go); };
}

let products: Record<string, string> | null = null;
/** what the server said about this phone's till: null until it has answered */
let storeOn: boolean | null = null;
let loading: Promise<void> | null = null;
/** A failed answer is NOT kept: cached as an empty map it used to mark every engine "not on sale" for
 *  the rest of the page's life after one bad bar of signal. */
function loadProducts(): Promise<void> {
  if (products) return Promise.resolve();
  if (!loading) {
    loading = (async () => {
      try {
        // the platform matters: the two stores are meant to share a product id, but the server allows one
        // to differ (IAP_PRODUCT_ANDROID_*), so it must be told which shop is asking
        const r = await fetch(`/api/iap/products?platform=${storeOf() === 'google' ? 'android' : 'ios'}`);
        if (!r.ok) return;
        const j = (await r.json()) as { products?: Record<string, string>; store?: boolean };
        products = j.products ?? {};
        storeOn = j.store !== false;
      } catch { /* asked again next time */ }
    })().finally(() => { loading = null; });
  }
  return loading;
}
/** the store's product id for this engine: '' when it is not sold in the app, null when we could not ask */
async function productFor(carId: string): Promise<string | null> {
  await loadProducts();
  if (!products) return null;
  return products[carId] || '';
}

/** whether this engine can be bought in this app at all, before anybody taps (null = cannot tell yet).
 *  Named on our side is not enough: the STORE must have the product too. An engine whose product is
 *  mapped here before App Store Connect has it says "Coming to the app" until the day it does, and then
 *  goes on sale by itself, with no deploy in between. */
export async function iapOnSale(carId: string): Promise<boolean | null> {
  if (!iapAvailable()) return null;
  const p = await productFor(carId);
  if (p === null) return null;
  if (p === '') return false;
  const v = await askOnce(carId);
  return v === null ? null : v !== '';
}

function send(msg: string): void {
  try { window.RoarBridge?.postMessage(msg); } catch { /* older app */ }
}

/**
 * Runs the whole purchase: the store's sheet, our server's verification, the unlock.
 * @returns 'ok' | 'cancelled' | an error message
 */
export async function buyEngine(carId: string): Promise<'ok' | 'cancelled' | string> {
  if (!iapAvailable()) return 'not available here';
  const product = await productFor(carId);
  if (product === null) return 'we could not reach ROARDRIVE';
  if (!product) return NOT_ON_SALE;
  return new Promise((resolve) => {
    let done = false;
    const finish = (r: 'ok' | 'cancelled' | string) => { if (!done) { done = true; window.removeEventListener('roariap', onEvent as EventListener); resolve(r); } };
    const onEvent = async (ev: Event) => {
      const e = (ev as CustomEvent<IapEvent>).detail;
      if (!e || (e.product && e.product !== product && e.type !== 'error')) return;
      if (e.type === 'cancelled') return finish('cancelled');
      if (e.type === 'error') return finish(e.message || 'the purchase failed');
      if (e.type !== 'purchased') return;   // 'pending' just waits
      const proof = proofFor(e, carId);
      if (!proof) return;                   // a 'purchased' with nothing to prove it: not ours to act on
      try {
        const r = await fetch(proof.url, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(proof.body),
        });
        if (!r.ok) {
          const j = (await r.json().catch(() => ({}))) as { reason?: string; error?: string };
          return finish(j.reason || j.error || 'we could not verify the purchase');
        }
        addPurchase(carId);
        send(`iap:ack:${product}`);   // now the store may close the transaction
        finish('ok');
      } catch {
        finish('we could not reach ROARDRIVE to confirm the purchase');
      }
    };
    window.addEventListener('roariap', onEvent as EventListener);
    send(`iap:buy:${product}`);
    setTimeout(() => finish(`${storeOf() === 'google' ? 'Google Play' : 'the App Store'} did not answer`), 120000);
  });
}

/**
 * What Apple charges for THIS engine, in the buyer's currency. Every car is its own product with its own
 * price (ten engines will be ten products), so the button must say Apple's price, not the web's.
 * Resolves to '' when there is no store to ask, and the caller keeps the web price.
 */
const priceAsked = new Map<string, Promise<string | null>>();
const priceKnown = new Map<string, string>();
/** the store's price already in hand, for a first frame that does not flash the web's price first */
export function iapPriceNow(carId: string): string { return priceKnown.get(carId) || ''; }
/** ONE QUESTION PER ENGINE PER PAGE. Every surface asked StoreKit again on mount, and StoreKit takes a
 *  couple of seconds: the glass opened saying the web's 4,99 € and the pit box a beat later said the
 *  store's own price, so one engine had two prices. The answer is kept (an empty one is asked again). */
export function iapPrice(carId: string): Promise<string> {
  if (!iapAvailable()) return Promise.resolve('');
  return askOnce(carId).then((v) => v || '');
}
/** the store's answer, kept: a price, '' (the store said it has no such product), or null (no answer,
 *  which is asked again next time) */
function askOnce(carId: string): Promise<string | null> {
  let p = priceAsked.get(carId);
  if (!p) {
    p = askPrice(carId).then((v) => { if (v) priceKnown.set(carId, v); if (v === null) priceAsked.delete(carId); return v; });
    priceAsked.set(carId, p);
  }
  return p;
}
async function askPrice(carId: string): Promise<string | null> {
  const product = await productFor(carId);
  if (product === null) return null;
  if (!product) return '';
  return new Promise((resolve) => {
    let done = false;
    const finish = (p: string | null) => { if (!done) { done = true; window.removeEventListener('roariap', onEvent as EventListener); resolve(p); } };
    const onEvent = (ev: Event) => {
      const e = (ev as CustomEvent<IapEvent & { price?: string }>).detail;
      // app 1.0.0+17 answers an empty price for a product the store says it does not have; older builds
      // say nothing, and the timeout below leaves the question open
      if (e && e.type === 'price' && e.product === product) finish(e.price || '');
    };
    window.addEventListener('roariap', onEvent as EventListener);
    send(`iap:price:${product}`);
    setTimeout(() => finish(null), 6000);
  });
}

export interface RestoreResult {
  ok: boolean;
  /** engines that came back (server-verified) */
  cars: string[];
  /** for us, not for the driver */
  message?: string;
}

/**
 * "I already bought this." Apple requires a restore that anyone can find, and it must actually give the
 * engine back — so this closes the whole loop: Apple replays every transaction of that Apple ID, each one
 * goes to our server, which checks Apple's signature and writes the purchase down again. Only then is the
 * transaction acknowledged, and the engine is unlocked on this device (reinstall, new phone, same account).
 *
 * Outside the app there is no App Store to ask: purchases live on our server against this device and the
 * buyer's email, so a restore is a re-sync — and signing in with the purchase email brings back the rest.
 */
export async function restorePurchases(): Promise<RestoreResult> {
  if (!iapAvailable()) {
    const before = new Set(getPurchases());
    await syncEntitlements();
    return { ok: true, cars: getPurchases().filter((c) => !before.has(c)) };
  }
  return new Promise((resolve) => {
    const cars = new Set<string>();
    let done = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const finish = (message?: string) => {
      if (done) return;
      done = true;
      if (timer) clearTimeout(timer);
      window.removeEventListener('roariap', onEvent as EventListener);
      resolve({ ok: !message, cars: [...cars], message });
    };
    // Apple answers over a few seconds and says nothing at all when there is nothing to replay:
    // the restore is over when the transactions stop arriving.
    const settle = (ms: number) => { if (timer) clearTimeout(timer); timer = setTimeout(() => finish(), ms); };
    const onEvent = async (ev: Event) => {
      const e = (ev as CustomEvent<IapEvent>).detail;
      if (!e || done) return;
      if (e.type === 'error') return finish(e.message || 'the restore failed');
      if (e.type !== 'purchased') return; // 'price' / 'pending' are not ours
      // no carId: the server reads the store's product id and tells us which engine it is
      const proof = proofFor(e);
      if (!proof) return;
      settle(8000); // one arrived — more may still be on the way
      try {
        const r = await fetch(proof.url, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(proof.body),
        });
        if (r.ok) {
          const j = (await r.json()) as { carId?: string };
          if (j.carId) { addPurchase(j.carId); cars.add(j.carId); }
          if (e.product) send(`iap:ack:${e.product}`); // now the store may close the transaction
        }
      } catch {
        /* offline: the purchase stays with the store and the next restore picks it up */
      }
      settle(5000);
    };
    window.addEventListener('roariap', onEvent as EventListener);
    send('iap:restore');
    settle(12000);
  });
}
